import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { isRouteMatchingLocation, RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { RelativeStore } from "./RelativeStore";

class RelativeHandler extends BaseOperationHandler {
    constructor() {
        super();
        this.RegisterOperation(LocalOperations.Relative.CloseFile, this.closeFile, false);
        this.RegisterOperation(LocalOperations.Relative.OpenFile, this.openFile, true);
        this.RegisterOperation(LocalOperations.Relative.SwitchProfileTab, this.switchProfileTab, true);
        this.RegisterOperation(LocalOperations.Relative.EditProfile, this.editProfile, true);
        this.RegisterOperation(LocalOperations.Relative.CancelProfileEdition, this.cancelEdition, true);
        this.RegisterOperation(LocalOperations.Relative.SaveProfileState, this.saveProfileState, true);
        this.RegisterOperation(LocalOperations.Relative.MarkAsReadProfileMemo, this.markAsReadProfileMemo, true);
    }

    private cancelEdition(request: LocalEvent, store: RelativeStore): void {
        const file = store.GetCurrentFile();
        if (!file) {
            throw new Error("The current relative does not have any relativeFile in the store.");
        }
        file.isEditing = false;
        file.ActiveTabSavedState = null;
        store.relativeFiles[store.selectedPersonContactId] = { ...file };
    }

    private editProfile(request: LocalEvent, store: RelativeStore): void {
        const file = store.GetCurrentFile();
        if (!file) {
            throw new Error("The current relative does not have any relativeFile in the store.");
        }
        file.isEditing = true;
        file.ActiveTabSavedState = null;
        store.relativeFiles[store.selectedPersonContactId] = { ...file };
    }

    private closeFile(request: LocalEvent, store: RelativeStore): void {
        const redirect = request.Get<boolean>("redirect");

        // Clean store
        delete store.relatives[store.selectedPersonContactId];
        delete store.relativeFiles[store.selectedPersonContactId];
        store.selectedPersonContactId = null;

        if (redirect) {
            window.setTimeout(() => RouterHistory.push("calendar"), 0);
            store.EmitChangeToListeners();
        }
    }

    private openFile(request: LocalEvent, store: RelativeStore): void {
        const contactId = request.Get<string>("contactId");
        const person = store.relatives[contactId];

        if (!person) {
            throw new Error("The event contactId does not match any relative from the store.");
        }

        const hasMemoNote: boolean = !IsNullOrWhiteSpace(person.memoNote);

        store.selectedPersonContactId = person.ContactId;

        if (!store.relativeFiles[store.selectedPersonContactId])
            store.relativeFiles[store.selectedPersonContactId] = {
                ActiveTab: ProfileTab.Demographic,
                isEditing: false,
                ActiveTabSavedState: null,
                ShowProfileMemoPopup: hasMemoNote
            };

        // Redirect to patient tab
        cacheStore.UpdateTab(MyleTabKey.Patients);
        if (!isRouteMatchingLocation("patients")) {
            RouterHistory.push("patients");
        }
    }

    private saveProfileState(request: LocalEvent, store: RelativeStore): void {
        const state = request.Get<any>("state");
        const tab = request.Get<any>("tab");
        const file = store.GetCurrentFile();
        if (!file) {
            throw new Error("The current relative does not have any relativeFile in the store.");
        }

        if (tab !== file.ActiveTab) {
            return;
        }

        file.ActiveTabSavedState = state;
    }

    private switchProfileTab(request: LocalEvent, store: RelativeStore): void {
        const tab = request.Get<ProfileTab>("tab");
        const isEditing = request.Get<boolean>("isEditing");
        const file = store.GetCurrentFile();
        if (!file) {
            throw new Error("The current relative does not have any relativeFile in the store.");
        }

        if (tab === file.ActiveTab) {
            return;
        }

        file.ActiveTab = tab;
        file.ActiveTabSavedState = null;
        file.isEditing = isEditing;
        store.relativeFiles[store.selectedPersonContactId] = { ...file };
    }

    private markAsReadProfileMemo(request: LocalEvent, store: RelativeStore): void {
        const file = store.GetCurrentFile();

        if (!file) {
            throw new Error("The current relative does not have any relativeFile in the store.");
        }

        file.ShowProfileMemoPopup = false;
        store.relativeFiles[store.selectedPersonContactId] = { ...file };
    }
}

export { RelativeHandler };
